import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import ExplicitTag from '../../../Components/GeneralComponents/ExplicitTag'
import classes from './CollectionCard.module.css'

export default function CollectionCard({
  collection,
  index,
  onIdChange,
  onRemove,
  isDuplicate,
}) {
  const [isEditing, setIsEditing] = useState(false)
  const [editedId, setEditedId] = useState(collection.f_id)

  useEffect(() => {
    setEditedId(collection.f_id)
  }, [collection.f_id])

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: collection.f_id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  }

  const handleIdChange = (e) => {
    setEditedId(e.target.value)
  }

  const handleIdBlur = async () => {
    setIsEditing(false)
    if (editedId !== collection.f_id && editedId.trim()) {
      const success = await onIdChange(index, editedId.trim())
      if (!success) {
        setEditedId(collection.f_id)
      }
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur()
    }
    if (e.key === 'Escape') {
      setEditedId(collection.f_id)
      setIsEditing(false)
    }
  }

  const imageUrl = collection.image?.url || collection.header?.background?.url || null

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={classes.card}
    >
      <div className={classes.dragHandle} {...attributes} {...listeners}>
        <span className={classes.dragIcon}>&#8942;&#8942;</span>
      </div>

      <div className={classes.positionBadge}>{index + 1}</div>

      {isDuplicate && <div className={classes.dupeBadge}>DUPE</div>}

      <button
        className={classes.openButton}
        onClick={() => window.open(`/collections/${collection.f_id}`, '_blank')}
        title="Open collection"
      >
        &#8599;
      </button>

      <button
        className={classes.removeButton}
        onClick={() => onRemove(index)}
        title="Remove collection"
      >
        &times;
      </button>

      <div className={classes.imageContainer}>
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={collection.name}
            className={classes.coverImage}
            draggable={false}
          />
        ) : (
          <div className={classes.placeholderImage}>
            <span>No Image</span>
          </div>
        )}

        <div className={classes.nameOverlay}>
          {collection.is_explicit && <ExplicitTag size="xsmall" />}
          <span className={classes.collectionName}>{collection.name || 'Untitled'}</span>
        </div>
      </div>

      <div className={classes.idContainer}>
        <input
          type="text"
          value={isEditing ? editedId : collection.f_id}
          onChange={handleIdChange}
          onFocus={() => setIsEditing(true)}
          onBlur={handleIdBlur}
          onKeyDown={handleKeyDown}
          className={classes.idInput}
          placeholder="Collection ID"
        />
      </div>
    </div>
  )
}

CollectionCard.propTypes = {
  collection: PropTypes.shape({
    f_id: PropTypes.string.isRequired,
    name: PropTypes.string,
    image: PropTypes.shape({
      url: PropTypes.string
    }),
    header: PropTypes.shape({
      background: PropTypes.shape({
        url: PropTypes.string
      })
    }),
    is_explicit: PropTypes.bool
  }).isRequired,
  index: PropTypes.number.isRequired,
  onIdChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  isDuplicate: PropTypes.bool,
}
